import React from 'react';
import { Box, Flex } from 'rebass';

import NarrowContainer from '../../components/NarrowContainer/NarrowContainer';
import ThinContainer from '../../components/ThinContainer/ThinContainer';
import WideContainer from '../../components/WideContainer/WideContainer';

import OpacityController from '../../components/OpacityController/OpacityController';
import { TypographyContextProvider } from '../../components/Typography/Typography';
import colourMatching from '../../utils/colourMatching';
import {
  parseMargin,
  parsePadding,
  parseSpacing,
} from '../../utils/parseSpacing';
import renderStoryblokComponents from '../../utils/renderStoryblokComponents';
import { LayoutSectionStoryblok } from '../../types/storyblok';

const containerMap = {
  full: Box,
  narrow: NarrowContainer,
  thin: ThinContainer,
  wide: WideContainer,
};

/**
 * Renders a <LayoutSection /> component
 * @param {Object} props
 * @param {string} props.adornmentLocation
 * @param {string} props.adornmentPosition
 * @param {string} props.colourScheme
 * @param {string} props.direction
 * @param {string} props.isFade - Should apply Fade in logic
 * @param {string} [props.margin]
 * @param {string} [props.padding]
 * @param {string} props.slots - Layout slots
 * @param {string} props.topSlot - Top layout slot
 * @param {string} props.bottomSlot - Bottom layout slot
 * @param {"full"|"thin"|"wide"} props.width - Container width
 */
const LayoutSection = ({
  adornmentLocation,
  adornmentPosition,
  colourScheme,
  direction,
  isFade,
  margin,
  padding,
  slots = [],
  topSlot = [],
  bottomSlot = [],
  width,
}: LayoutSectionStoryblok) => {
  const SlotsContainer = containerMap[width];

  return (
    <TypographyContextProvider value={{ colourScheme }}>
      <Box
        as="section"
        backgroundColor={colourMatching(colourScheme)}
        sx={{
          ...parseMargin(margin),
          ...parsePadding(padding),
        }}
        className="max-w-standard !mx-auto"
      >
        <OpacityController
          container={SlotsContainer}
          shouldFade={isFade}
          sx={{
            position: 'relative',
          }}
        >
          <Box>{renderStoryblokComponents({ blocks: topSlot })}</Box>
          <Flex
            flexDirection={parseSpacing(direction)}
            flexWrap={parseSpacing(direction).map((direction) =>
              direction === 'column' || direction === 'column-reverse'
                ? 'nowrap'
                : 'wrap'
            )}
            width="100%"
          >
            {renderStoryblokComponents({ blocks: slots })}
          </Flex>
          <Box>{renderStoryblokComponents({ blocks: bottomSlot })}</Box>
        </OpacityController>
      </Box>
    </TypographyContextProvider>
  );
};

export default LayoutSection;
